import {
  AtSign,
  AudioLines,
  Disc3,
  Facebook,
  Ghost,
  Github,
  Globe,
  Headphones,
  Image,
  Instagram,
  Linkedin,
  MessageCircle,
  Music,
  Music2,
  Radio,
  Send,
  Twitch,
  Twitter,
  Youtube,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Catalog of link services a creator can attach to their public page.
 *
 * The UI shows a dropdown, prefills the base URL and only asks for the
 * handle/slug, so filling a profile takes seconds instead of copy-pasting URLs.
 * Music services accept ARTIST pages only — track, album and playlist links are
 * rejected client-side before we ever hit the network.
 */
export type PlatformDef = {
  key: string;
  label: string;
  /** Base URL the handle is appended to. Empty for the manual "other" entry. */
  prefix: string;
  /** Hint shown inside the handle input. */
  placeholder: string;
  group: "social" | "music" | "other";
  /** Regex the final URL must match (artist-page enforcement for music). */
  requires?: RegExp;
  /** Message shown when `requires` fails. */
  requiresHint?: string;
};

const ARTIST_ONLY = "Only artist pages are accepted — not tracks, albums or playlists.";

export const PLATFORM_CATALOG: PlatformDef[] = [
  // Social
  { key: "instagram", label: "Instagram", prefix: "https://instagram.com/", placeholder: "yourhandle", group: "social" },
  { key: "tiktok", label: "TikTok", prefix: "https://tiktok.com/@", placeholder: "yourhandle", group: "social" },
  { key: "x", label: "X / Twitter", prefix: "https://x.com/", placeholder: "yourhandle", group: "social" },
  { key: "youtube", label: "YouTube", prefix: "https://youtube.com/@", placeholder: "yourchannel", group: "social" },
  { key: "facebook", label: "Facebook", prefix: "https://facebook.com/", placeholder: "yourpage", group: "social" },
  { key: "threads", label: "Threads", prefix: "https://threads.net/@", placeholder: "yourhandle", group: "social" },
  { key: "linkedin", label: "LinkedIn", prefix: "https://linkedin.com/in/", placeholder: "yourname", group: "social" },
  { key: "twitch", label: "Twitch", prefix: "https://twitch.tv/", placeholder: "yourchannel", group: "social" },
  { key: "snapchat", label: "Snapchat", prefix: "https://snapchat.com/add/", placeholder: "yourhandle", group: "social" },
  { key: "pinterest", label: "Pinterest", prefix: "https://pinterest.com/", placeholder: "yourhandle", group: "social" },
  { key: "telegram", label: "Telegram", prefix: "https://t.me/", placeholder: "yourchannel", group: "social" },
  { key: "discord", label: "Discord", prefix: "https://discord.gg/", placeholder: "invitecode", group: "social" },
  { key: "github", label: "GitHub", prefix: "https://github.com/", placeholder: "yourname", group: "social" },
  { key: "kick", label: "Kick", prefix: "https://kick.com/", placeholder: "yourchannel", group: "social" },

  // Music — artist pages only
  {
    key: "spotify",
    label: "Spotify (artist)",
    prefix: "https://open.spotify.com/artist/",
    placeholder: "artistId",
    group: "music",
    requires: /open\.spotify\.com\/artist\/[A-Za-z0-9]+/i,
    requiresHint: ARTIST_ONLY,
  },
  {
    key: "apple_music",
    label: "Apple Music (artist)",
    prefix: "https://music.apple.com/artist/",
    placeholder: "your-name/123456789",
    group: "music",
    requires: /music\.apple\.com\/.*artist\//i,
    requiresHint: ARTIST_ONLY,
  },
  {
    key: "deezer",
    label: "Deezer (artist)",
    prefix: "https://www.deezer.com/artist/",
    placeholder: "1234567",
    group: "music",
    requires: /deezer\.com\/(?:[a-z]{2}\/)?artist\/\d+/i,
    requiresHint: ARTIST_ONLY,
  },
  {
    key: "beatport",
    label: "Beatport (artist)",
    prefix: "https://www.beatport.com/artist/",
    placeholder: "your-name/123456",
    group: "music",
    requires: /beatport\.com\/artist\//i,
    requiresHint: ARTIST_ONLY,
  },
  {
    key: "tidal",
    label: "TIDAL (artist)",
    prefix: "https://tidal.com/artist/",
    placeholder: "1234567",
    group: "music",
    requires: /tidal\.com\/(?:browse\/)?artist\/\d+/i,
    requiresHint: ARTIST_ONLY,
  },
  {
    key: "amazon_music",
    label: "Amazon Music (artist)",
    prefix: "https://music.amazon.com/artists/",
    placeholder: "B0XXXXXXX",
    group: "music",
    requires: /music\.amazon\.[a-z.]+\/artists\//i,
    requiresHint: ARTIST_ONLY,
  },
  {
    key: "youtube_music",
    label: "YouTube Music (channel)",
    prefix: "https://music.youtube.com/channel/",
    placeholder: "UCxxxxxxxx",
    group: "music",
    requires: /music\.youtube\.com\/channel\//i,
    requiresHint: ARTIST_ONLY,
  },
  { key: "soundcloud", label: "SoundCloud", prefix: "https://soundcloud.com/", placeholder: "yourname", group: "music" },
  { key: "bandcamp", label: "Bandcamp", prefix: "https://", placeholder: "yourname.bandcamp.com", group: "music" },
  { key: "audiomack", label: "Audiomack", prefix: "https://audiomack.com/", placeholder: "yourname", group: "music" },
  { key: "mixcloud", label: "Mixcloud", prefix: "https://mixcloud.com/", placeholder: "yourname", group: "music" },
  { key: "beatstars", label: "BeatStars", prefix: "https://beatstars.com/", placeholder: "yourname", group: "music" },

  // Manual
  { key: "other", label: "Other (paste full link)", prefix: "", placeholder: "https://your-platform.com/you", group: "other" },
];

export const PLATFORM_GROUP_LABELS: Record<PlatformDef["group"], string> = {
  social: "Social",
  music: "Music & streaming",
  other: "Other",
};

export function platformDef(key: string) {
  return PLATFORM_CATALOG.find((p) => p.key === key);
}

export const CATALOG_LABELS: Record<string, string> = Object.fromEntries(
  PLATFORM_CATALOG.map((p) => [p.key, p.label.replace(/ \((artist|channel)\)$/, "")]),
);

/** Build and validate the final URL from a dropdown choice plus a handle. */
export function buildPlatformUrl(key: string, raw: string): { url: string } | { error: string } {
  const def = platformDef(key);
  const value = raw.trim().replace(/^@/, "");
  if (!value) return { error: "Add your handle or link first." };

  let url: string;
  if (!def || def.key === "other") {
    url = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  } else if (/^https?:\/\//i.test(value)) {
    url = value; // user pasted a full link anyway — accept it
  } else {
    url = `${def.prefix}${value}`;
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return { error: "That link is not valid." };
    }
  } catch {
    return { error: "That link is not valid." };
  }

  if (def?.requires && !def.requires.test(url)) {
    return { error: def.requiresHint ?? "That link is not valid for this service." };
  }
  return { url };
}

/** Lucide icon per platform key, used on the link cards. */
export const PLATFORM_ICONS: Record<string, LucideIcon> = {
  instagram: Instagram,
  tiktok: Music2,
  x: Twitter,
  youtube: Youtube,
  facebook: Facebook,
  threads: AtSign,
  linkedin: Linkedin,
  twitch: Twitch,
  snapchat: Ghost,
  pinterest: Image,
  telegram: Send,
  discord: MessageCircle,
  github: Github,
  kick: Radio,
  spotify: Music,
  apple_music: Music,
  deezer: Music,
  beatport: Disc3,
  tidal: Music,
  amazon_music: Music,
  youtube_music: Youtube,
  soundcloud: AudioLines,
  bandcamp: Disc3,
  audiomack: Headphones,
  mixcloud: Headphones,
  beatstars: Disc3,
  other: Globe,
};

export function platformIcon(key: string): LucideIcon {
  return PLATFORM_ICONS[key] ?? Globe;
}

/** Music/streaming links show artist name + link only — no follower or post counts. */
export function isMusicPlatform(key: string) {
  return platformDef(key)?.group === "music";
}
